import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@zg-rentals/react-query5';

import type { FieldValues } from '@zillow/cosmos';
import { CosmosRenderer, ReviewRenderer } from '@zillow/cosmos';
import { Box, Button, Divider, Heading, Modal, Tag, TextButton } from '@zillow/constellation-10';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';

import Lease from '../../models/Lease';
import Property from '../../models/Property';
import { useLeasingUrlParams } from '../../hooks/useLeasingUrlParams';
import TenantInformation_cosmos from '../../components/Sections/PartiesToTheLease/TenantInformation_cosmos';
import type CosmosTenantSection from '../../models/CosmosTenantSection';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import CancelSigningConfirmation from '../../components/SectionWrapper/CancelSigningConfirmation';
import { AdditionalDocuments } from '../../components/Sections';
import { onPrepareForLeaseSigningMobileHandoff } from '../../utils/mobileHandoffFunctions';
import { MOBILE_HANDOFF_TYPES } from '../../constants/stringConstants';
import {
  getCosmosJourneyToken,
  useLeaseSummaryQuery,
  usePropertyInfoQuery,
  useReevaluateLeaseCompletionMutation,
} from '../../utils/queries';
import { StyledLoadingMask } from '../../constants/styleConstants';
import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';
import ManageYourLeaseUpsell, { shouldShowManageLeaseUpsell } from '../../components/Widgets/ManageYourLeaseUpsell';
import { CosmosValuesProvider, useCosmosValues } from '../../contexts/CosmosValuesContext';
import { SectionProvider, useSectionContext } from '../../contexts/SectionContext';

const CUSTOM_COSMOS_TENANT_STEP_ID = 'tenants';

function TenantInformationWithRef({
  setHasError,
  setOnDataSubmit,
}: {
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  setOnDataSubmit: (fn: () => FieldValues) => void;
}) {
  const { section } = useSectionContext();

  useEffect(() => {
    if (!section) {
      setOnDataSubmit(() => ({}));
      return;
    }

    const capturedFieldValues = JSON.parse(
      JSON.stringify((section as CosmosTenantSection).getCosmosFieldValues()),
    ) as FieldValues;

    setOnDataSubmit(() => capturedFieldValues);
  }, [section, setOnDataSubmit]);

  return <TenantInformation_cosmos setHasError={setHasError} />;
}

type CosmosViewInnerProps = {
  lease: Lease;
};

function CosmosViewInner({ lease }: CosmosViewInnerProps) {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { leaseId, propertyId, viewType } = useLeasingUrlParams();
  const [showCancelSigningConfirmation, setShowCancelSigningConfirmation] = useState(false);
  const [isThereAnErrorOnCustomScreen, setIsThereAnErrorOnCustomScreen] = useState(false);

  const onDataSubmitRef = useRef<(() => FieldValues) | null>(null);

  const propertyQuery = usePropertyInfoQuery(propertyId, !!propertyId);

  const leaseSummaryQuery = useLeaseSummaryQuery(Number(leaseId), LeaseArtifactType.LEASE);

  const reevaluateLeaseCompletionMutation = useReevaluateLeaseCompletionMutation();

  const propertyData = propertyQuery.data;
  const property = propertyData ? new Property({ ...propertyData, propertyId }) : null;
  const summaryActions = leaseSummaryQuery.data?.summaryActions || [];
  const { setCosmosValues } = useCosmosValues();

  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  const isLoading = isFetching > 0 || isMutating > 0;

  function generateJourneyToken() {
    return getCosmosJourneyToken(LeaseArtifactType.LEASE, Number(leaseId));
  }

  function onCosmosDataLoaded(fieldValues: FieldValues) {
    setCosmosValues(fieldValues);
  }

  async function onDataSubmitted() {
    await reevaluateLeaseCompletionMutation.mutateAsync({
      postData: {
        leaseArtifactType: LeaseArtifactType.LEASE,
        leaseArtifactId: leaseId,
      },
    });

    queryClient.invalidateQueries({
      predicate: (query) => query.meta?.tags?.includes('leasing:lease-status-change'),
    });
  }

  function onClose() {
    history.push(Lease.getDashboardPath());
    onPrepareForLeaseSigningMobileHandoff(MOBILE_HANDOFF_TYPES.RETURN_TO_DASHBOARD);
  }

  function onPreSubmit(id: string) {
    if (id === CUSTOM_COSMOS_TENANT_STEP_ID) {
      return !isThereAnErrorOnCustomScreen;
    }
    return true;
  }

  if (!lease || !property) {
    return null;
  }

  return (
    <>
      {lease.isEditable() ? (
        <Modal.Root
          size="fullScreen"
          open
          onOpenChange={(open) => {
            if (!open) {
              onClose();
            }
          }}
          useDismissProps={{ escapeKey: false, outsidePress: false }}
        >
          <Modal.Portal>
            <Modal.Backdrop>
              <Modal.Content css={{ padding: '0 !important' }}>
                <Modal.Body css={{ padding: '0 !important' }}>
                  <CosmosRenderer
                    journeyId={lease.journeyId}
                    journeyVersionId={lease.journeyVersionId}
                    generateJourneyToken={generateJourneyToken}
                    stepId={viewType === 'list' ? '__REVIEW__' : undefined}
                    header={
                      <Box>
                        <Heading level={1} css={{ textStyle: 'responsive.heading-sm' }}>
                          Lease for {property.streetAddress}
                        </Heading>
                        <Tag tone={lease.getBadgeColor()} size="sm" css={{ textTransform: 'capitalize' }}>
                          {lease.getHumanReadableStatus()}
                        </Tag>
                      </Box>
                    }
                    customSteps={[
                      {
                        stepId: CUSTOM_COSMOS_TENANT_STEP_ID,
                        component: (
                          <StyledLoadingMask loading={isLoading}>
                            <SectionProvider>
                              <TenantInformationWithRef
                                setHasError={setIsThereAnErrorOnCustomScreen}
                                setOnDataSubmit={(fn) => {
                                  onDataSubmitRef.current = fn;
                                }}
                              />
                            </SectionProvider>
                          </StyledLoadingMask>
                        ),
                        onDataSubmit: () => {
                          if (onDataSubmitRef.current) {
                            const fieldValues = onDataSubmitRef.current();
                            setCosmosValues(fieldValues);
                            return fieldValues;
                          }
                          return {};
                        },
                      },
                      {
                        stepId: 'additional-documents',
                        component: (
                          <StyledLoadingMask loading={isLoading}>
                            <AdditionalDocuments />
                          </StyledLoadingMask>
                        ),
                        onDataSubmit: () => null,
                      },
                    ]}
                    onDataLoaded={onCosmosDataLoaded}
                    onDataSubmitted={onDataSubmitted}
                    reviewNextElement={
                      <LeaseActionButtonGroup
                        leaseActions={summaryActions}
                        lease={lease}
                        property={property}
                        data-test="summary-action"
                        isTop={false}
                      />
                    }
                    onSaveAndExit={onClose}
                    onPreSubmit={onPreSubmit}
                  />
                </Modal.Body>
              </Modal.Content>
            </Modal.Backdrop>
          </Modal.Portal>
        </Modal.Root>
      ) : (
        <>
          <Modal.Root
            size="fullScreen"
            open
            onOpenChange={(open) => {
              if (!open) {
                onClose();
              }
            }}
            useDismissProps={{ escapeKey: false, outsidePress: false }}
          >
            <Modal.Portal>
              <Modal.Backdrop>
                <Modal.Content>
                  <Modal.Header
                    css={{
                      display: 'flex',
                      paddingLeft: { base: 'layout.tight', lg: 'layout.loosest' },
                      paddingRight: { base: 'layout.tight', lg: 'layout.loosest' },
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Heading level={1} css={{ textStyle: 'responsive.body-lg-bold' }}>
                        Lease for {property.streetAddress}
                      </Heading>
                      <Tag tone={lease.getBadgeColor()} size="sm">
                        {lease.getHumanReadableStatus()}
                      </Tag>
                    </Box>
                    <Button tone="neutral" emphasis="outlined" onClick={onClose}>
                      Exit
                    </Button>
                  </Modal.Header>
                  <Divider />
                  <Modal.Body>
                    <Box
                      css={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(12, 1fr)',
                        marginTop: { base: 'none', lg: 'layout.looser' },
                        marginBottom: 'layout.tight',
                      }}
                    >
                      <Box css={{ gridColumnEnd: { base: 'span 0', lg: 'span 2', xl: 'span 3' } }} />
                      <Box css={{ gridColumnEnd: { base: 'span 12', lg: 'span 8', xl: 'span 6' } }}>
                        {lease?.isEligibleForLeaseRenewal() && shouldShowManageLeaseUpsell(lease) && (
                          <Box css={{ marginBottom: 'layout.tight' }}>
                            <ManageYourLeaseUpsell lease={lease} />
                          </Box>
                        )}
                        <ReviewRenderer
                          journeyId={lease.journeyId}
                          journeyVersionId={lease.journeyVersionId}
                          generateJourneyToken={generateJourneyToken}
                          isReadOnly
                        />
                      </Box>
                      <Box css={{ gridColumnEnd: { base: 'span 0', lg: 'span 2', xl: 'span 3' } }} />
                    </Box>
                  </Modal.Body>
                  <Divider />
                  <Modal.Footer
                    css={{
                      display: 'flex',
                      paddingLeft: { base: 'layout.tight', lg: 'layout.loosest' },
                      paddingRight: { base: 'layout.tight', lg: 'layout.loosest' },
                      alignItems: 'center',
                    }}
                  >
                    <LeaseActionButtonGroup
                      additionalButtons={
                        lease.isInSigningState() && (
                          <TextButton onClick={() => setShowCancelSigningConfirmation(true)}>Edit lease</TextButton>
                        )
                      }
                      leaseActions={summaryActions}
                      lease={lease}
                      property={property}
                      data-test="summary-action"
                      isTop={false}
                    />
                  </Modal.Footer>
                </Modal.Content>
              </Modal.Backdrop>
            </Modal.Portal>
          </Modal.Root>
          {showCancelSigningConfirmation && (
            <CancelSigningConfirmation
              leaseId={Number(leaseId)}
              onClose={() => {
                setShowCancelSigningConfirmation(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
}

// Wrapper component that provides the Cosmos values context
type CosmosViewProps = {
  lease: Lease;
};

function CosmosView({ lease }: CosmosViewProps) {
  return (
    <CosmosValuesProvider>
      <CosmosViewInner lease={lease} />
    </CosmosValuesProvider>
  );
}

export default CosmosView;
